// ============================================================
// Viashift — Role helpers (Phase 1, Epic 1B.5)
// ============================================================
// Centralized role permission policy. Every component that gates
// behavior on role (banners, conditional buttons, future route
// guards) reads from here so the policy lives in one place.
//
// Permission model: strictly hierarchical. Every owner capability
// includes manager, every manager capability includes staff. We
// don't have feature lists per role today — if/when product needs
// "manager-but-not-owner" or non-tiered capabilities, replace this
// hierarchy with an explicit feature map and migrate consumers.
//
// Source of truth for role values is `clinic_members.role` in the
// schema (CHECK constraint pins it to owner/manager/staff). Mirror
// any future role addition to ROLE_HIERARCHY here in the same
// migration cycle.
// ============================================================

import type { ClinicRole } from './session';

export const ROLE_HIERARCHY: Record<ClinicRole, number> = {
  staff: 1,
  manager: 2,
  owner: 3,
};

/**
 * True if `role` meets or exceeds `required` in the hierarchy.
 * Examples:
 *   isAtLeast('owner', 'manager')  → true
 *   isAtLeast('manager', 'manager') → true
 *   isAtLeast('staff', 'manager')   → false
 */
export function isAtLeast(role: ClinicRole, required: ClinicRole): boolean {
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[required];
}

/**
 * Roles that can be required as a gate. `staff` is the floor — gating
 * something to `staff` is a no-op since every authenticated user has
 * at least staff role. Excluding it from the type prevents the
 * meaningless call site `<RoleRequiredBanner required="staff" />`.
 */
export type GateRole = Exclude<ClinicRole, 'staff'>;
